import { useAgGridDefaults } from "@/lib/ag-grid-defaults";
import { useServerGrid } from "@/lib/use-server-grid";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import { manufacturingClient } from "@/lib/manufacturing-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import { AgGridReact } from "@ag-grid-community/react";
import type { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import type { ProductionOrder } from "@/gen/omnios/v1/manufacturing_pb";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	useCancelProductionOrder,
	useReleaseProductionOrder,
	useStartProductionOrder,
} from "@/hooks/use-manufacturing";

export const Route = createFileRoute("/manufacturing/production")({
	component: ProductionPage,
});

function StatusCell({ value }: ICellRendererParams) {
	const colors: Record<string, string> = {
		planned: "secondary",
		released: "default",
		in_progress: "default",
		completed: "outline",
		cancelled: "destructive",
	};
	return <Badge variant={(colors[value] ?? "secondary") as never}>{value}</Badge>;
}

function ProductionPage() {
	const { t } = useTranslation("manufacturing");
	const gridDefaults = useAgGridDefaults();
	const release = useReleaseProductionOrder();
	const start = useStartProductionOrder();
	const cancel = useCancelProductionOrder();
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const columns = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				pinned: "left",
				sortable: false,
				filter: false,
				resizable: false,
			},
			{
				field: "productId",
				headerName: t("field_product"),
				width: 130,
				pinned: "left",
				valueFormatter: (p: { value: string }) => p.value?.slice(0, 8) ?? "",
			},
			{
				field: "quantity",
				headerName: t("field_qty"),
				width: 100,
				type: "numericColumn",
				filter: "agNumberColumnFilter",
				valueFormatter: (p: { value: number }) =>
					Number(p.value).toLocaleString("ru-RU"),
			},
			{ field: "uom", headerName: t("field_uom"), width: 60 },
			{
				field: "status",
				headerName: t("field_status"),
				width: 120,
				cellRenderer: StatusCell,
				filter: false,
			},
			{
				field: "plannedStart",
				headerName: t("field_planned_start"),
				width: 110,
				filter: "agDateColumnFilter",
			},
			{
				field: "plannedEnd",
				headerName: t("field_planned_end"),
				width: 110,
				filter: "agDateColumnFilter",
			},
			{
				field: "workCenterId",
				headerName: t("field_work_center"),
				width: 130,
				valueFormatter: (p: { value: string }) =>
					p.value ? p.value.slice(0, 8) : "—",
			},
			{
				field: "yieldQty",
				headerName: t("field_yield"),
				width: 90,
				type: "numericColumn",
				filter: "agNumberColumnFilter",
				valueFormatter: (p: { value: number }) =>
					p.value > 0 ? Number(p.value).toLocaleString("ru-RU") : "—",
			},
			{
				field: "scrapQty",
				headerName: t("field_scrap"),
				width: 90,
				type: "numericColumn",
				filter: "agNumberColumnFilter",
				valueFormatter: (p: { value: number }) =>
					p.value > 0 ? Number(p.value).toLocaleString("ru-RU") : "—",
			},
			{ field: "priority", headerName: t("field_priority"), width: 80 },
		],
		[t],
	);

	const { datasource, totalRows, refreshGrid, gridRef } = useServerGrid<
		ProductionOrder & { id: string }
	>({
		fetchRows: async ({ pageSize, pageToken }) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC not yet in generated proto types
			const resp = await (manufacturingClient as any).listProductionOrders({
				page: { pageSize, pageToken },
				productId: "",
				status: "",
				workCenterId: "",
			});
			return {
				rows: resp.orders as (ProductionOrder & { id: string })[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		pageSize: 100,
		colToField: {
			productId: "product_id",
			plannedStart: "planned_start",
			plannedEnd: "planned_end",
			workCenterId: "work_center_id",
			yieldQty: "yield_qty",
			scrapQty: "scrap_qty",
		},
	});

	const statusBar = useMemo(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("status_bar_rows")}`,
				align: "left" as const,
			},
		],
		[totalRows, t],
	);

	useWorkspacePage({ statusBar });

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						for (const id of selectedIds) release.mutate({ id });
					}}
					disabled={selectedIds.length === 0}
				>
					{t("action_release")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						for (const id of selectedIds) start.mutate({ id });
					}}
					disabled={selectedIds.length === 0}
				>
					{t("action_start")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						for (const id of selectedIds) {
							cancel.mutate({ id, reason: "Отменено вручную" });
						}
						setSelectedIds([]);
					}}
					disabled={selectedIds.length === 0}
				>
					{t("action_cancel")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={() => {
						toast.info(
							"Заказы на производство создаются автоматически из утверждённых плановых действий типа 'производство'",
						);
					}}
				>
					<Plus className="h-4 w-4 mr-1" />
					{t("menu_new_production_order")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz">
				<AgGridReact
					ref={gridRef}
					{...gridDefaults}
					columnDefs={columns}
					rowModelType="infinite"
					datasource={datasource}
					cacheBlockSize={100}
					rowSelection="multiple"
					suppressRowClickSelection
					onSelectionChanged={() => {
						const selected = gridRef.current?.api
							?.getSelectedRows()
							.map((r: { id: string }) => r.id);
						setSelectedIds(selected ?? []);
					}}
					onSortChanged={() => refreshGrid()}
					getRowId={(params) => params.data?.id}
				/>
			</div>
		</div>
	);
}
